import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import axios from 'axios';
import NotFound from '../pages/NotFound';

import { API } from '../config/api';

/**
 * PublicPageGuard - Wraps public pages and checks if they are published
 * If unpublished and user is not admin, shows 404 page immediately
 * If admin, shows the page with a "DRAFT" banner
 */
const PublicPageGuard = ({ children, user }) => {
  const location = useLocation();
  const [status, setStatus] = useState({ loading: true, published: true, pageInfo: null });
  const currentPath = location.pathname;
  // Accept both admin and super_admin roles
  const isAdmin = user?.role === 'admin' || user?.role === 'super_admin';

  useEffect(() => {
    const checkVisibility = async () => {
      try {
        const response = await axios.get(`${API}/pages/visibility`);
        const pages = response.data.pages || [];
        
        // Find matching page by path
        const page = pages.find(p => {
          // Exact match
          if (p.path === currentPath) return true;
          // Match base path for dynamic routes
          const pageBase = p.path.split('/').filter(Boolean)[0];
          const currentBase = currentPath.split('/').filter(Boolean)[0];
          return pageBase && currentBase && pageBase === currentBase;
        });

        // If page exists in visibility system, check its status
        if (page) {
          setStatus({ loading: false, published: page.is_published, pageInfo: page });
        } else {
          // Page not in visibility system - assume published
          setStatus({ loading: false, published: true, pageInfo: null });
        }
      } catch (error) {
        console.error('Error checking page visibility:', error);
        // On error, allow access
        setStatus({ loading: false, published: true, pageInfo: null });
      }
    };

    checkVisibility();
  }, [currentPath]);

  // Loading state
  if (status.loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  // Page is unpublished and user is not admin - show 404
  if (!status.published && !isAdmin) {
    return <NotFound />;
  }

  // Admin viewing unpublished page - show with draft banner
  if (!status.published && isAdmin) {
    return (
      <div className="relative">
        {/* Fixed Draft Banner at Top */}
        <div className="fixed top-0 left-0 right-0 z-[200] bg-gradient-to-r from-amber-500 to-orange-500 text-white py-2 px-4 text-center font-medium shadow-lg">
          <div className="flex items-center justify-center gap-3">
            <svg className="w-5 h-5 animate-pulse" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
            </svg>
            <span className="font-bold">DRAFT MODE</span>
            <span>— This page is unpublished. Only admins can see it.</span>
            <span className="px-3 py-0.5 bg-white/20 rounded-full text-sm">
              {status.pageInfo?.name || currentPath}
            </span>
          </div>
        </div>
        {/* Content with top padding */}
        <div className="pt-12">
          {children}
        </div>
      </div>
    );
  }

  // Page is published - render normally
  return children;
};

export default PublicPageGuard;
